import React from 'react';

export interface EmployerScoreGaugeProps {
  score: number;
  size?: number;
  strokeWidth?: number;
  /** Ring + number color. Defaults to currentColor so it inherits a BrandPanel's `--panel-on`. */
  color?: string;
  trackColor?: string;
  className?: string;
  label?: React.ReactNode;
  /** Overrides the centered digit with this text instead (e.g. a tier grade like "B"). The
   * ring itself still fills proportional to `score`. Used for the actual Employer Score so we
   * never print a specific number an employer could dispute; callers that reuse this same gauge
   * to visualize a pillar *weight* (e.g. "31%") don't pass this and keep the numeric digit. */
  displayText?: string;
}

/**
 * Shared radial employer-score gauge (0-100). Used on the best-companies podium and the
 * company profile Employer Scorecard so both read from one visual language instead of a
 * flat numeric badge in one place and a progress bar in another.
 */
export function EmployerScoreGauge({
  score,
  size = 64,
  strokeWidth = 7,
  color = 'currentColor',
  trackColor = 'rgba(255,255,255,0.28)',
  className = '',
  label,
  displayText,
}: EmployerScoreGaugeProps) {
  const r = (size - strokeWidth) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, score));
  const offset = circumference * (1 - clamped / 100);
  return (
    <div className={`relative inline-flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={c} cy={c} r={r} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ color }}>
        <span
          className={`font-display font-bold ${displayText ? '' : 'tabular-nums'}`}
          style={{ fontSize: displayText && displayText.length > 2 ? size * 0.22 : size * 0.28 }}
        >
          {displayText ?? Math.round(clamped)}
        </span>
        {label ? <span className="text-[9px] font-semibold uppercase tracking-wide opacity-80">{label}</span> : null}
      </div>
    </div>
  );
}
